<template>
  <AppBody :subtitle="$t('marketing.invitation.title')" :actions="actions">
    <CardModule untitle>
      <ExpansionPanelFilters>
        <v-form
          ref="form"
          @submit.prevent="filterInvitations"
          class="search-forms"
        >
          <v-row>
            <v-col cols="12" md="3" sm="4">
              <BaseTextField
                v-model="filter.template"
                label="Template"
                :placeholder="$t('marketing.invitation.insert-template-name')"
                clearable
              />
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <BaseTextField
                v-model="filter.subject"
                :label="$t('marketing.invitation.subject')"
                :placeholder="$t('marketing.invitation.insert-subject')"
                clearable
              />
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <BaseSelect
                v-model="filter.inviteType"
                :label="$t('marketing.invitation.invitation-type')"
                :placeholder="$t('marketing.invitation.select-invitation-type')"
                :persistent-placeholder="false"
                :items="inviteTypeOptions"
                @input="handleInviteType"
                item-text="text"
                item-value="value"
                clearable
              />
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <BaseAutocomplete
                v-model="filter.event"
                :label="$t('marketing.invitation.event')"
                :placeholder="$t('marketing.invitation.select-events')"
                :persistent-placeholder="false"
                :disabled="!filter.inviteType || isProducerType"
                :loading="eventsLoading"
                :items="allEvents"
                item-text="descricao"
                item-value="idproduto"
                multiple
                clearable
              />
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <DateTimePicker
                :label="$t('marketing.invitation.initial-date')"
                placeholder="dd/mm/aaaa"
                @formatted-date="handleInitDateTime"
                clearable
              />
            </v-col>
            <v-col cols="12" md="3" sm="4">
              <DateTimePicker
                :label="$t('marketing.invitation.final-date')"
                placeholder="dd/mm/aaaa"
                @formatted-date="handleFinalDateTime"
                clearable
              />
            </v-col>
            <v-col class="d-flex justify-end">
              <BaseButton variant="filter" data-cy="filtrar" type="submit">
                {{ $t("events.filter") }}
              </BaseButton>
            </v-col>
          </v-row>
        </v-form>
      </ExpansionPanelFilters>

      <v-data-table
        class="table-body"
        :no-data-text="$t('marketing.invitation.no-invitations-register')"
        :loading-text="$t('common.searching-info')"
        :headers="headers"
        :items="invitations || []"
        :loading="loadingInvitations"
        :items-per-page="10"
        :footer-props="{
          itemsPerPageOptions: [10, 20, 50],
          itemsPerPageText: $t('marketing.invitation.invitations-per-page'),
        }"
        mobile-breakpoint="0"
      >
        <template v-slot:item.finalizado="{ item }">
          {{
            item.finalizado == 1
              ? $t("marketing.invitation.definitive")
              : $t("marketing.invitation.draft")
          }}
        </template>
        <template v-slot:item.data_cadastro="{ item }">
          {{ formatDateTime(item.data_cadastro) || "-" }}
        </template>
        <template v-slot:item.actions="{ item }">
          <div style="display: flex; justify-content: center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :color="$config.dados.corinputs"
                  height="30"
                  width="30"
                  small
                  icon
                  v-bind="attrs"
                  v-on="on"
                  class="mr-2"
                  :disabled="item.finalizado == '1'"
                  @click="editInvitation(item.idemail_marketing)"
                >
                  <v-icon>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("common.edit") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :color="$config.dados.corinputs"
                  height="30"
                  width="30"
                  small
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="openAddReceiversModal(item.idemail_marketing)"
                  class="mr-2"
                >
                  <v-icon>mdi-send-circle-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("marketing.invitation.send-invitations") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :color="$config.dados.corinputs"
                  height="30"
                  width="30"
                  small
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="openConfirmationModal(item.idemail_marketing)"
                  class="mr-2"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("common.delete") }}</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </CardModule>

    <AddReceivers
      v-if="addReceiversModal"
      v-model="addReceiversModal"
      :marketingEmailId="marketingEmailId"
      @close="addReceiversModal = false"
    />
    <ConfirmAction
      :dialog.sync="confirmationModal"
      :title="$t('marketing.invitation.confirm-delete')"
      @action="confirmDelete"
      @close="confirmationModal = false"
    />
  </AppBody>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { formatDateTime } from "../../utils";
import AppBody from "../../layouts/components/AppBody.vue";
import CardModule from "../../components/cards/CardModule.vue";
import ExpansionPanelFilters from "../../components/forms/ExpansionPanelFilters.vue";
import BaseTextField from "../../components/forms/BaseTextField.vue";
import BaseSelect from "../../components/forms/BaseSelect.vue";
import BaseAutocomplete from "../../components/forms/BaseAutocomplete.vue";
import DateTimePicker from "../../components/forms/DateTimePicker.vue";
import BaseButton from "../../components/forms/BaseButton.vue";
import AddReceivers from "../../components/marketing/invitation/AddReceivers.vue";
import ConfirmAction from "../../components/events/EventSettingsModal/ConfirmAction.vue";

export default {
  metaInfo() {
    const title = this.$t("marketing.invitation.title");
    return {
      title: `${title} | ElevenTickets`,
      meta: [
        {
          content: `${title} | ElevenTickets`,
        },
      ],
    };
  },
  components: {
    AppBody,
    CardModule,
    ExpansionPanelFilters,
    BaseTextField,
    BaseSelect,
    BaseAutocomplete,
    DateTimePicker,
    BaseButton,
    AddReceivers,
    ConfirmAction,
  },
  data() {
    return {
      formatDateTime,
      addReceiversModal: false,
      confirmationModal: false,
      marketingEmailId: "",
      actions: [
        {
          name: this.$t("marketing.invitation.new-invitation"),
          action: () =>
            this.$router.push({
              name: "invitationSettings",
              params: { type: "cadastrar" },
            }),
        },
      ],
      filter: {
        template: "",
        subject: "",
        inviteType: "",
        event: "",
        initialDate: "",
        finalDate: "",
      },
      inviteTypeOptions: [
        {
          text: this.$t("marketing.invitation.event"),
          value: "EVENTO",
        },
        {
          text: this.$t("marketing.invitation.producer"),
          value: "PRODUTOR",
        },
      ],
      headers: [
        {
          text: "Template",
          align: "start",
          value: "nome",
          width: 140,
        },
        {
          text: this.$t("marketing.invitation.subject"),
          align: "start",
          value: "assunto",
          width: 140,
        },
        {
          text: this.$t("marketing.invitation.creation-date-time"),
          align: "start",
          value: "data_cadastro",
          width: 180,
        },
        {
          text: this.$t("marketing.invitation.type"),
          align: "start",
          value: "tipo",
          width: 100,
        },
        {
          text: this.$t("marketing.invitation.event"),
          align: "start",
          value: "descricao",
          width: 140,
        },
        {
          text: this.$t("marketing.invitation.total-sendings"),
          align: "end",
          value: "envios",
          width: 140,
        },
        {
          text: "Status",
          align: "center",
          value: "finalizado",
          width: 100,
        },
        {
          text: this.$t("common.actions"),
          align: "center",
          value: "actions",
          width: 120,
          sortable: false,
        },
      ],
    };
  },
  computed: {
    ...mapGetters([
      "allEvents",
      "eventsLoading",
      "invitations",
      "loadingInvitations",
      "storeSequencial",
    ]),
    isProducerType() {
      return this.filter.inviteType === "PRODUTOR";
    },
  },
  methods: {
    ...mapActions(["fetchInvitations", "deleteInvitation", "fetchEvents"]),
    handleInviteType() {
      if (this.isProducerType) this.filter.event = "";

      if (!this.isProducerType && !this.allEvents.length) {
        this.fetchEvents({
          producerId: this.storeSequencial,
        });
      }
    },
    filterInvitations() {
      let filters = {};

      if (this.filter.template) {
        Object.assign(filters, { nome: this.filter.template });
      }
      if (this.filter.subject) {
        Object.assign(filters, { assunto: this.filter.subject });
      }
      if (this.filter.inviteType) {
        Object.assign(filters, { tipo: this.filter.inviteType });
      }
      if (this.filter.event) {
        Object.assign(filters, { idprodutos: this.filter.event });
      }
      if (this.filter.initialDate) {
        Object.assign(filters, { dt_inicial: this.filter.initialDate });
      }
      if (this.filter.finalDate) {
        Object.assign(filters, { dt_final: this.filter.finalDate });
      }

      this.fetchInvitations(filters);
    },
    handleInitDateTime(date) {
      this.filter.initialDate = date;
    },
    handleFinalDateTime(date) {
      this.filter.finalDate = date;
    },
    async editInvitation(marketingEmailId) {
      this.$router.push({
        name: "invitationSettings",
        params: { type: "editar", id: marketingEmailId },
      });
    },
    openAddReceiversModal(marketingEmailId) {
      this.marketingEmailId = marketingEmailId;
      this.addReceiversModal = true;
    },
    openConfirmationModal(marketingEmailId) {
      this.confirmationModal = true;
      this.marketingEmailId = marketingEmailId;
    },
    confirmDelete() {
      this.deleteInvitation(this.marketingEmailId);
      this.confirmationModal = false;
    },
  },
  mounted() {
    this.fetchInvitations();
  },
};
</script>

<style lang="scss" scoped></style>
